import { flatMap } from "lodash";
import {
  AssessmentDeliveryFragment,
  AssessmentDeliveryTemplateFragment,
  AssessmentGroupDelivery,
  AssessmentQuestionResponseVisibility,
  AssessmentType,
} from "types/graphql-schema";

import { UseLabelType } from "@apps/use-label/use-label";
import { assertEdgesNonNull, assertNonNull } from "@helpers/helpers";

import { AssessmentSection } from "./components/questions/types";

export const bgClassName = "bg-white rounded-lg shadow-md";

export const ASSESSMENT_QUESTION_SCALE_MIN = 1;
export const ASSESSMENT_QUESTION_SCALE_MAX = 10;

export const getAssessmentQuestionScale = (question: {
  startValue?: number | null;
  endValue?: number | null;
}) => {
  const scaleStartValue = Math.max(1, question.startValue ?? 1);
  const scaleEndValue = Math.min(
    ASSESSMENT_QUESTION_SCALE_MAX,
    question.endValue ?? 5
  );
  return {
    scaleEndValue,
    scaleStartValue,
    scaleDiff: scaleEndValue - scaleStartValue + 1,
  };
};

export const getAssessmentSections = (
  assessmentTemplate: AssessmentDeliveryTemplateFragment,
  additionalQuestionSets: NonNullable<
    AssessmentDeliveryFragment["additionalQuestionSets"]
  >
) => {
  const assessmentType = assessmentTemplate.assessmentType;
  const addtionalSections = flatMap(
    assessmentType === AssessmentType.EngagementSurvey
      ? []
      : assertEdgesNonNull(additionalQuestionSets[assessmentType]),
    (set) => {
      const newSections: AssessmentSection[] = assertEdgesNonNull(
        set.sections
      ).map((section) => {
        const questions = assertEdgesNonNull(section.questions);
        return {
          id: section.id,
          title: section.title,
          description: section.description,
          questions,
          subjectVisibility: section.subjectVisibility,
        };
      });
      return newSections;
    }
  );

  const questionSets = assertEdgesNonNull(assessmentTemplate?.questionSets);
  const regularQuestionSets = questionSets.filter((questionSet) => {
    const sections = assertEdgesNonNull(questionSet.sections);
    return sections.some((section) => {
      const questions = assertEdgesNonNull(section.questions);
      return !questions.some(
        (question) =>
          question.question.__typename ===
          "TalentIndicatorAssessmentQuestionNode"
      );
    });
  });
  const talentIndicatorQuestionSets = questionSets.filter((questionSet) => {
    const sections = assertEdgesNonNull(questionSet.sections);
    return sections.some((section) => {
      const questions = assertEdgesNonNull(section.questions);
      return questions.some(
        (question) =>
          question.question.__typename ===
          "TalentIndicatorAssessmentQuestionNode"
      );
    });
  });
  const regularSections = flatMap(regularQuestionSets, (set) => {
    const newSections: AssessmentSection[] = assertEdgesNonNull(
      set.sections
    ).map((section) => {
      const questions = assertEdgesNonNull(section.questions);
      return {
        id: section.id,
        title: section.title,
        description: section.description,
        questions,
        subjectVisibility: section.subjectVisibility,
      };
    });
    return newSections;
  });
  const talentIndicatorSections = flatMap(
    talentIndicatorQuestionSets,
    (set) => {
      const newSections: AssessmentSection[] = assertEdgesNonNull(
        set.sections
      ).map((section) => {
        const questions = assertEdgesNonNull(section.questions);
        return {
          id: section.id,
          title: section.title,
          description: section.description,
          questions,
          subjectVisibility: section.subjectVisibility,
        };
      });
      return newSections;
    }
  );
  return [...regularSections, ...addtionalSections, ...talentIndicatorSections];
};

export const getHiddenQuestionCount = (
  assessmentTemplate: AssessmentDeliveryTemplateFragment,
  assessmentDelivery: AssessmentDeliveryFragment,
  delivery: AssessmentGroupDelivery
) => {
  const sections = getAssessmentSections(
    assessmentTemplate,
    assertNonNull(assessmentDelivery.additionalQuestionSets)
  );
  const questions = flatMap(sections, (section) => section.questions);
  const answers = assertEdgesNonNull(assessmentDelivery.answers);
  return questions.filter(
    (node) =>
      (node.question.responseVisibility ===
        AssessmentQuestionResponseVisibility.HiddenFromSubject ||
        delivery === AssessmentGroupDelivery.None) &&
      answers
        .filter(
          (answer) => answer.responder?.id !== assessmentDelivery.target?.id
        )
        .some((answer) => answer.questionId === node.question.id)
  ).length;
};

export const assessmentTypeSectionHeadings: (
  label: UseLabelType,
  combinedSelfLabels: boolean,
  isSelfAssessment?: boolean
) => {
  [key in AssessmentType]: string;
} = (
  label: UseLabelType,
  combinedSelfLabels: boolean,
  isSelfAssessment?: boolean
) => ({
  [AssessmentType.Performance]: `${
    combinedSelfLabels
      ? "Self and Manager"
      : isSelfAssessment
      ? "Self"
      : "Manager"
  } ${label("review", {
    capitalize: true,
  })}`,
  [AssessmentType.Manager]: `Upward ${label("review", {
    capitalize: true,
  })} from Direct Reports`,
  [AssessmentType.Peer]: `Peer ${label("review", { capitalize: true })}`,
  [AssessmentType.EngagementSurvey]: "Survey",
});
